import { CheckCircle, Warning, Info, ErrorOutline, Close } from '@mui/icons-material';
import { Box, Snackbar, Slide } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setToastMessage } from 'src/store/my_pages/app';
import { AppState } from 'src/store/Store';

const iconMapping = {
  check_circle: CheckCircle,
  error: ErrorOutline,
  warning: Warning,
  info: Info,
  success: CheckCircle,
};

const colorConfig = {
  success: {
    iconColor: '#10B981',
    iconBg: '#D1FAE5',
    progressColor: '#10B981',
    defaultSubtitle: '',
  },
  error: {
    iconColor: '#EF4444',
    iconBg: '#FEE2E2',
    progressColor: '#EF4444',
    defaultSubtitle: '',
  },
  warning: {
    iconColor: '#F59E0B',
    iconBg: '#FEF3C7',
    progressColor: '#F59E0B',
    defaultSubtitle: '',
  },
  info: {
    iconColor: '#3B82F6',
    iconBg: '#DBEAFE',
    progressColor: '#3B82F6',
    defaultSubtitle: '',
  },
};

const ToastMessage = () => {
  const { toastMessage } = useSelector((state: AppState) => state.appGeneralReducer);
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const backupTimerRef = useRef<NodeJS.Timeout | null>(null);
  const remainingTimeRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  // Get the appropriate color based on the toastMessage color
  const toastColor = (toastMessage.color || 'warning') as keyof typeof colorConfig;
  const config = colorConfig[toastColor] || colorConfig.warning;

  // Get the corresponding icon component for the toast
  const IconComponent = iconMapping[toastMessage.iconKey as keyof typeof iconMapping] || iconMapping[toastColor] || iconMapping.warning;

  // Get subtitle - use provided subtitle or default based on color
  const subtitle = (toastMessage as any).subtitle || config.defaultSubtitle;

  // Handle auto-close when Snackbar closes
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(setToastMessage({ ...toastMessage, show: false }));
  };

  // Calculate autoHideDuration value - disable when hovered
  const autoHideDuration = toastMessage.autoClose && toastMessage.show && !isHovered ? toastMessage.duration || 3000 : null;

  // Progress bar animation - only reset when toast first shows
  useEffect(() => {
    if (!toastMessage.show || !toastMessage.autoClose) {
      setProgress(100);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }

    // Only reset progress when toast first shows, not on hover changes
    setProgress(100);
    remainingTimeRef.current = toastMessage.duration || 3000;
    startTimeRef.current = Date.now();

    const duration = toastMessage.duration || 3000;
    const interval = 50; // Update every 50ms
    const decrement = (100 / duration) * interval;

    const updateProgress = () => {
      setProgress((prev) => {
        const newProgress = prev - decrement;
        if (newProgress <= 0) {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          return 0;
        }
        return newProgress;
      });
    };

    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(updateProgress, interval);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [toastMessage.show, toastMessage.autoClose, toastMessage.duration]);

  // Backup timer in case Snackbar's autoHideDuration doesn't work
  // Only set when toast first shows - hover handlers will manage pausing/resuming
  useEffect(() => {
    if (!toastMessage.show || !toastMessage.autoClose) {
      if (backupTimerRef.current) {
        clearTimeout(backupTimerRef.current);
        backupTimerRef.current = null;
      }
      return;
    }

    const duration = toastMessage.duration || 3000;

    // Clear existing timer
    if (backupTimerRef.current) {
      clearTimeout(backupTimerRef.current);
    }

    // Always use full duration for backup timer on initial show
    // (hover handlers manage pause/resume with remainingTimeRef)
    backupTimerRef.current = setTimeout(() => {
      dispatch(setToastMessage({ ...toastMessage, show: false }));
    }, duration);

    return () => {
      if (backupTimerRef.current) {
        clearTimeout(backupTimerRef.current);
        backupTimerRef.current = null;
      }
    };
  }, [toastMessage.show, toastMessage.autoClose, toastMessage.duration, toastMessage.msg, dispatch]);

  // Handle hover events
  const handleMouseEnter = () => {
    setIsHovered(true);
    // Pause timers - just clear them, don't reset progress
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (backupTimerRef.current) {
      clearTimeout(backupTimerRef.current);
      backupTimerRef.current = null;
    }
    // Calculate and store remaining time based on current progress (freeze it)
    const duration = toastMessage.duration || 3000;
    remainingTimeRef.current = (progress / 100) * duration;
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Resume progress bar from current position
    if (toastMessage.show && toastMessage.autoClose && remainingTimeRef.current > 0) {
      const interval = 50;
      // Calculate decrement based on remaining time to complete the countdown
      const currentProgress = progress;
      const decrement = (currentProgress / remainingTimeRef.current) * interval;

      const updateProgress = () => {
        setProgress((prev) => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            return 0;
          }
          return newProgress;
        });
      };

      // Resume progress bar animation
      progressIntervalRef.current = setInterval(updateProgress, interval);

      // Resume backup timer with remaining time
      backupTimerRef.current = setTimeout(() => {
        dispatch(setToastMessage({ ...toastMessage, show: false }));
      }, remainingTimeRef.current);
    }
  };

  return (
    <Snackbar
      key={toastMessage.msg + toastMessage.show}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={toastMessage.show}
      onClose={handleClose}
      slots={{ transition: Slide }}
      slotProps={{
        transition: {
          direction: 'top',
        },
      }}
      sx={{
        marginTop: 5,
      }}
    >
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          width: '400px',
          maxWidth: '90vw',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: '16px 20px',
          overflow: 'hidden',
        }}
      >
        {/* Close Button */}
        <Box
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            cursor: 'pointer',
            color: '#6B7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            '&:hover': {
              color: '#374151',
            },
          }}
        >
          <Close sx={{ fontSize: '18px' }} />
        </Box>

        {/* Content */}
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', pr: '24px' }}>
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: config.iconBg,
              flexShrink: 0,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: '8px',
                backgroundColor: config.iconColor,
                opacity: 0.1,
              },
            }}
          >
            <IconComponent
              sx={{
                fontSize: '24px',
                color: config.iconColor,
                zIndex: 1,
              }}
            />
          </Box>

          {/* Text Content */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'start' }}>
            <Box
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
                // lineHeight: '24px',
                // mb: '4px',
              }}
            >
              {toastMessage.msg}
            </Box>
            {subtitle && (
              <Box
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#6B7280',
                  lineHeight: '20px',
                }}
              >
                {subtitle}
              </Box>
            )}
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '3px',
            width: `${progress}%`,
            backgroundColor: config.progressColor,
            transition: 'width 0.05s linear',
            borderRadius: '0 0 12px 12px',
          }}
        />
      </Box>
    </Snackbar>
  );
};

export default ToastMessage;
